import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Zap, Radio, Download, ExternalLink, Copy, Check, BookOpen } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import PageHeader from '@/components/PageHeader'

/* Streamdeck-config hub — één plek voor:
   - Companion web-UI openen
   - Auth-token invullen
   - Companion-config downloaden per show
   - Docs uit de wiki bereiken

   Loskoppelen van SetupHubPage want dit is een aparte configuratie-flow. */
export default function StreamdeckConfigPage() {
  const [token, setToken] = useState('')
  const [copied, setCopied] = useState(null)

  // Companion draait op dezelfde host als de lanserver-frontend, op poort 8000.
  // Op de LAN kan het IP wijzigen; gebruik daarom het hostname van de huidige
  // browser-sessie i.p.v. een hardcoded IP.
  const companionHost = typeof window !== 'undefined' && window.location.hostname.endsWith('.ts.net')
    ? 'https://companion.tail9a4b92.ts.net'
    : (typeof window !== 'undefined'
        ? `${window.location.protocol}//${window.location.hostname}:8000`
        : '')
  const base = typeof window !== 'undefined' ? window.location.origin : ''

  const exportUrl = (scope) => {
    const t = token || 'REPLACE_WITH_ADMIN_JWT'
    return `${base}/api/streamdeck/companion-export?scope=${scope}&token=${encodeURIComponent(t)}&base_url=${encodeURIComponent(base)}`
  }

  const copy = (txt, key) => {
    navigator.clipboard?.writeText(txt).then(() => {
      setCopied(key)
      setTimeout(() => setCopied(null), 1500)
    })
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <PageHeader
        icon={Zap}
        eyebrow="Setup · Streamdeck"
        title="Streamdeck-bediening"
        subtitle="Companion op lanserver, Satellite op je presentator-PC"
        actions={
          <Button asChild size="sm" variant="outline">
            <Link to="/wiki/streamdeck-integratie">
              <BookOpen className="h-4 w-4 mr-2" />
              Wiki
            </Link>
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Radio className="h-4 w-4 text-acid" /> 1 · Companion web-UI
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-3">
          <p>Companion draait al in Docker op lanserver. Open de web-UI om Surfaces te bekijken, buttons te configureren en logs te lezen.</p>
          <Button asChild variant="outline" size="sm">
            <a href={companionHost} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Companion op {companionHost.replace(/^https?:\/\//, '')}
            </a>
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Download className="h-4 w-4 text-acid" /> 2 · Satellite installeren
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>Op de PC waar je Streamdeck mk2 via USB in zit:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Download Companion Satellite (~40 MB) van <a href="https://user.bitfocus.io/download-satellite" target="_blank" rel="noopener noreferrer" className="text-acid hover:underline">user.bitfocus.io/download-satellite</a></li>
            <li>Sluit je Streamdeck aan</li>
            <li>Start Satellite; wijs "Server" naar <code className="text-foreground">lanserver.local:16622</code></li>
            <li>Streamdeck verschijnt automatisch onder <strong>Surfaces</strong> in Companion</li>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Copy className="h-4 w-4 text-acid" /> 3 · Auth-token
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>Companion belt lanserver met HTTP. Plak je huidige admin-token hieronder — de export-URLs krijgen 'm dan als query-param mee.</p>
          <div className="flex gap-2">
            <Input value={token} onChange={e => setToken(e.target.value)}
                   placeholder="Bearer JWT (localStorage.auth_token)"
                   className="font-mono text-xs" />
            <Button size="sm" variant="outline"
                    onClick={() => {
                      try { setToken(localStorage.getItem('auth_token') || '') } catch {}
                    }}>
              Lees uit sessie
            </Button>
          </div>
          <p className="text-xs">Je huidige sessie-token wordt bewaard in de browser onder <code>auth_token</code>. Voor langere-termijn setup: genereer een dedicated admin-token en zet 'm hier.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Download className="h-4 w-4 text-acid" /> 4 · Companion-config downloaden
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-3">
          <p>Per show een JSON — importeer in Companion via <strong>Import / Export</strong> → <strong>Import</strong>.</p>
          {!token && (
            <div className="rounded-md border border-signal/40 bg-signal/10 px-3 py-2 text-xs text-signal">
              <strong>Vul eerst je auth-token in stap 3.</strong> Zonder token wordt <code>REPLACE_WITH_ADMIN_JWT</code> als
              placeholder gebruikt en werken de knop-triggers niet in Companion. Beter: zet <code>STREAMDECK_SECRET</code> in
              je .env, dan bakt de export een scoped secret in ipv een 7-dagen superadmin-JWT.
            </div>
          )}
          {['kempenvision', 'imagecompo'].map(scope => (
            <div key={scope} className="flex items-center gap-2 flex-wrap">
              <span className="w-32 font-semibold capitalize">{scope === 'kempenvision' ? 'Kempenvision' : 'Image Compo'}</span>
              <Button asChild size="sm" disabled={!token} className={!token ? 'pointer-events-none opacity-40' : ''}>
                <a href={token ? exportUrl(scope) : '#'} download={`companion-${scope}.json`}
                   aria-disabled={!token}>
                  <Download className="h-4 w-4 mr-1" /> Download JSON
                </a>
              </Button>
              <Button size="sm" variant="ghost" disabled={!token}
                      onClick={() => token && copy(exportUrl(scope), scope)}
                      title="Kopieer URL">
                {copied === scope ? <Check className="h-4 w-4 text-live" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          ))}
          <p className="text-xs">Elke JSON bevat 7 buttons in een 5×3-layout (Streamdeck mk2), alle acties gekoppeld aan de juiste HTTP-endpoints. Icons worden bij runtime opgehaald van de backend.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Doorlink</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          <Link to="/wiki/streamdeck-integratie" className="text-acid hover:underline flex items-center gap-1">
            <BookOpen className="h-3 w-3" /> Wiki-artikel Streamdeck-integratie (architectuur, endpoints, troubleshooting)
          </Link>
        </CardContent>
      </Card>
    </div>
  )
}
